<script setup lang="ts">
  import { computed } from 'vue';
  import type { CompiledFieldNode, FieldValue, SelectOption } from '@record-fill';
  import { SelectInput } from '@/shared/ui';
  import {
    FILL_SELECT_BUTTON_CLASSES,
    FILL_SELECT_EMPTY_TEXT_CLASSES,
    FILL_SELECT_FILLED_TEXT_CLASSES,
  } from '../../../model/dropdown-select-classes';
  import FillMultiSelectControl from './FillMultiSelectControl.vue';

  const DEFAULT_PLACEHOLDER = 'Selecione';

  const props = defineProps<{ node: CompiledFieldNode; disabled: boolean; readOnly?: boolean; inputClass?: string }>();
  const emit = defineEmits<{ (e: 'update', value: FieldValue): void }>();

  const control = computed(() => (props.node.control.kind === 'select' ? props.node.control : null));
  const options = computed<SelectOption[]>(() => control.value?.options ?? []);
  const multiple = computed(() => control.value?.multiple === true);

  const selected = computed<string[]>(() => {
    if (Array.isArray(props.node.value)) return props.node.value;
    return typeof props.node.value === 'string' && props.node.value.length > 0 ? [props.node.value] : [];
  });
  const isEmpty = computed(() => selected.value.length === 0);

  const placeholder = computed(() => {
    const text = control.value?.placeholder;
    return text !== undefined && text.length > 0 ? text : DEFAULT_PLACEHOLDER;
  });

  const singleValue = computed(() => selected.value[0] ?? '');

  const singleOptions = computed(() => {
    const mapped = options.value.map((option) => ({ id: option.value, name: option.label }));
    return isEmpty.value ? mapped : [{ id: '', name: placeholder.value }, ...mapped];
  });

  const singleButtonClasses = computed(() =>
    [FILL_SELECT_BUTTON_CLASSES, isEmpty.value ? FILL_SELECT_EMPTY_TEXT_CLASSES : FILL_SELECT_FILLED_TEXT_CLASSES].join(' '),
  );

  function onSingleSelect(value: string | number): void {
    emit('update', String(value));
  }
</script>

<template>
  <div
    class="w-full [font-family:var(--font-base)]"
    :data-field-id="node.id"
  >
    <!-- Read-only, the single select also takes the closed multiple trigger: it shows the chosen
         label with the chevron and never opens. -->
    <FillMultiSelectControl
      v-if="multiple || readOnly"
      :options="options"
      :model-value="selected"
      :placeholder="placeholder"
      :disabled="disabled"
      :read-only="readOnly"
      :label="node.label"
      @update="emit('update', $event)"
    />
    <SelectInput
      v-else
      :model-value="singleValue"
      :options="singleOptions"
      :label="placeholder"
      :aria-label="node.label"
      :disabled="disabled"
      :auto-select-first="false"
      use-teleport
      :button-class="singleButtonClasses"
      arrow-class="w-4 h-4"
      arrow-right="right-1.5"
      @update:model-value="onSingleSelect"
    />
  </div>
</template>
